import { getStationaryBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingStepUnavailablePage } from '@components/BookingFlow/Common/BookingStepUnavailablePage';
import { StationaryBookingSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { StationaryBookingScreen } from '@components/BookingFlow/Stationary/StationaryBookingScreen';
import { CustomerTimePickerNotices } from '@components/Common/CustomerTimePickerNotices';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { t } from '@lingui/core/macro';
import { customerRoutes } from 'noddi-util';
import { isStationaryHoldExpired, StationaryTimeSlotPicker, useShowSlotAvailability } from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useStationaryTimeSlotPicker } from '../../../../hooks/useStationaryTimeSlotPicker';
import {
  useServiceCenter,
  useStationaryAddonServices,
  useStationaryBookingActions,
  useStationaryPrimaryService,
  useStationaryReservation
} from '../../../../stores/StationaryBookingStore';

export const StationaryBookingTimeWindowPicker = () => {
  const { setReservation } = useStationaryBookingActions();
  const reservation = useStationaryReservation();
  const serviceCenter = useServiceCenter();
  const navigate = useNavigate();
  const showSlotAvailability = useShowSlotAvailability();

  const primarySelectedService = useStationaryPrimaryService();
  const addonServices = useStationaryAddonServices();
  const addonSalesItemIds = addonServices.map((s) => s.salesItemId);

  const {
    days,
    selectedSlotId,
    handleSelectSlot,
    goToPreviousRange,
    goToNextRange,
    isPreviousDisabled,
    isNextDisabled,
    dateRangeLabel,
    isPending,
    isReservationPending,
    pendingSlotId,
    error,
    translations
  } = useStationaryTimeSlotPicker({
    garageId: serviceCenter?.garageId,
    salesItemId: primarySelectedService?.salesItemId,
    addonSalesItemIds,
    reservation,
    onReservationChange: setReservation
  });

  const hasLiveHold = reservation != null && !isStationaryHoldExpired(reservation);

  // Capacity needs a service to size the slot, so without one the picker would stay pending
  // forever (deep link / back after deselecting). The Sentry noise that motivated the redirect
  // in #4984 is already gone — this logs at `debug` — so show the state instead of hiding it.
  if (!primarySelectedService) {
    return <BookingStepUnavailablePage missing='services' />;
  }

  return (
    <StationaryBookingScreen
      progress={getStationaryBookingProgress('time')}
      title={t`Choose date and time`}
      disableNextButton={!hasLiveHold || isReservationPending}
      nextButtonLoading={isReservationPending}
      // oxlint-disable-next-line typescript/no-misused-promises -- async onNextClicked runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
      onNextClicked={() => navigate(customerRoutes.newStationaryBookingSummary.getPath())}
      validationVariant='timeSlotRequired'
    >
      <StationaryBookingSpikedTireSeasonBanner />
      <CustomerTimePickerNotices surface='stationary' serviceDepartmentId={serviceCenter?.serviceDepartmentId} />
      {error ? (
        <ApiErrorPageWithTrans error={error} />
      ) : (
        <StationaryTimeSlotPicker
          days={days}
          selectedSlotId={selectedSlotId}
          onSelectSlot={handleSelectSlot}
          onNavigatePrevious={goToPreviousRange}
          onNavigateNext={goToNextRange}
          isPreviousDisabled={isPreviousDisabled}
          isNextDisabled={isNextDisabled}
          dateRangeLabel={dateRangeLabel}
          isPending={isPending}
          pendingSlotId={pendingSlotId}
          showAvailableCount={showSlotAvailability}
          translations={translations}
        />
      )}
    </StationaryBookingScreen>
  );
};
